import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { Printer, Check, AlertCircle, Phone, Mail } from 'lucide-react';
import { db } from './firebase.js';
import PublicBrandingHeader from './PublicBrandingHeader.jsx';
import PublicBreakdown from './PublicBreakdown.jsx';
import PublicJobPhotos from './PublicJobPhotos.jsx';
import PublicInspectionReport from './PublicInspectionReport.jsx';
import { formatInvoiceNumber } from './lib/job-number.js';

function formatPrice(n) {
  if (n == null || isNaN(n)) return '$0.00';
  return `$${Number(n).toFixed(2)}`;
}
function formatDate(ts) {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function PublicInvoice() {
  const { shareId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ref = doc(db, 'sharedInvoices', shareId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setNotFound(true);
        } else {
          setInvoice({ id: snap.id, ...snap.data({ serverTimestamps: 'estimate' }) });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Invoice load failed:', err);
        setError(err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [shareId]);

  if (loading) {
    return (
      <div className="public-shell">
        <div className="public-card"><p className="muted">Loading…</p></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="public-shell">
        <div className="public-card">
          <h1>Invoice not found</h1>
          <p className="muted">This link may be expired or incorrect.</p>
        </div>
      </div>
    );
  }

  const businessName = invoice.businessName || 'your mechanic';
  const hasContact = !!(invoice.businessPhone || invoice.businessEmail);
  // What the customer actually pays. Tax was frozen onto the invoice
  // when it was sent; grandTotal is absent on anything sent before tax
  // existed, so totalPrice remains the fallback.
  const invoiceDue = Number(invoice.grandTotal) || Number(invoice.totalPrice) || 0;

  return (
    <div className="public-shell">
      <div className="public-card">
        <PublicBrandingHeader doc={invoice} />

        <div className={`public-status-pill ${invoice.paid ? 'paid' : 'invoice'}`}>
          {invoice.paid
            ? <><Check size={12} strokeWidth={3} /> Paid</>
            : <><AlertCircle size={12} /> Payment due</>
          }
        </div>

        <p className="public-eyebrow">Invoice for</p>
        <h1>{invoice.customerName}</h1>
        {/* Same digits as the estimate this job started as. */}
        {formatInvoiceNumber(invoice.jobNumber) && (
          <p className="public-doc-number mono">{formatInvoiceNumber(invoice.jobNumber)}</p>
        )}

        <div className="public-section-block">
          <p className="public-section-label">What was done</p>
          <p className="public-description">{invoice.description}</p>
        </div>

        <PublicBreakdown share={invoice} />

        <PublicInspectionReport report={invoice.inspectionReport} />

        <PublicJobPhotos photos={invoice.jobPhotos} />

        <div className="public-total-block">
          <p className="public-section-label">{invoice.paid ? 'Total paid' : 'Total due'}</p>
          <p className="public-total-amount">{formatPrice(invoiceDue)}</p>
        </div>

        {invoice.paid ? (
          <div className="public-confirmation">
            <div className="public-check-mark">
              <Check size={36} strokeWidth={3} />
            </div>
            <h2>Thanks!</h2>
            <p>
              {businessName} has marked this invoice paid.
            </p>
            {invoice.paidAt && (
              <p className="public-confirmation-stamp mono">
                {formatDate(invoice.paidAt)}
              </p>
            )}
          </div>
        ) : (
          <div className="public-payment-due">
            <h2 className="public-payment-due-title">How to pay</h2>
            <p>
              Send {formatPrice(invoiceDue)} using the method {businessName} accepts
              (Venmo, cash, check, etc.). Your mechanic will mark this invoice paid once
              payment is received.
            </p>
            {hasContact && (
              <ul className="public-payment-contact">
                {invoice.businessPhone && (
                  <li>
                    <Phone size={14} />
                    <a href={`tel:${invoice.businessPhone}`}>{invoice.businessPhone}</a>
                  </li>
                )}
                {invoice.businessEmail && (
                  <li>
                    <Mail size={14} />
                    <a href={`mailto:${invoice.businessEmail}`}>{invoice.businessEmail}</a>
                  </li>
                )}
              </ul>
            )}
          </div>
        )}

        {error && <p className="form-error" style={{ marginTop: 12 }}>{error}</p>}

        <div className="public-print-row no-print">
          <button type="button" className="btn-secondary" onClick={() => window.print()}>
            <Printer size={14} /> Print or Save as PDF
          </button>
        </div>
      </div>

      <footer className="public-footer">
        {(invoice.businessName || invoice.businessPhone || invoice.businessEmail) ? (
          <p className="public-footer-from">
            Invoice from <strong>{invoice.businessName || 'your mechanic'}</strong>
          </p>
        ) : (
          <p className="public-footer-from">Sent through Bayless</p>
        )}
        <p className="public-attribution">
          <img src="/favicon.png?v=8" alt="" className="public-attribution-mark" />
          Powered by Bayless
        </p>
      </footer>
    </div>
  );
}
